import { useState } from 'react';
import { Inbox } from 'lucide-react';
import type { MyProgressResponse } from '@/lib/api-client';
import { useChartColors, type ChartSeries } from '@/hooks/ui/useChartColors';
import { Card, ProgressBar, SegmentedControl, Skeleton } from '@ai-tutor/ui';
import type { SegmentedOption } from '@ai-tutor/ui';

interface MyLessonSectionProps {
  data: MyProgressResponse | undefined;
  isLoading: boolean;
  /**
   * Which scope the card opens on. The scope is otherwise local state with no
   * prop and no callback, which made the entire Mindspace rollup — populated,
   * empty and loading — impossible to render without clicking the toggle.
   *
   * The catalog's `UI/` and `Components/` tiers are a static visual spec, so a
   * state that can only be reached by a click is a state that never gets a
   * visual diff. This is the standard uncontrolled-with-initial-value escape
   * hatch: the component still owns the scope after mount, and the production
   * call site in `ProgressLayout` does not pass it.
   */
  initialScope?: ActivityScope;
}

type ActivityScope = 'lesson' | 'mindspace';

const SCOPE_OPTIONS: readonly SegmentedOption<ActivityScope>[] = [
  { value: 'lesson', label: 'Lesson' },
  { value: 'mindspace', label: 'Mindspace' },
];

// `colorKey` indexes the theme-aware series palette (useChartColors) so the
// activity bars follow dark's lavender-forward tokens, not a baked-in indigo.
interface SectionGroup {
  key: string;
  label: string;
  colorKey: keyof ChartSeries;
  types: string[];
}

// Lesson-side groups follow the lesson panel's top-level tabs.
const LESSON_GROUPS: SectionGroup[] = [
  {
    key: 'lesson_content',
    label: 'Lesson content',
    colorKey: 's1',
    types: ['text', 'video', 'audio', 'podcast', 'mindmap', 'highlights'],
  },
  {
    key: 'practice',
    label: 'Practice',
    colorKey: 's2',
    types: ['practice_flashcard', 'practice_quiz'],
  },
  {
    key: 'notes',
    label: 'Notes',
    colorKey: 's3',
    types: ['notes'],
  },
  {
    key: 'reference',
    label: 'Reference',
    colorKey: 's4',
    types: ['reference'],
  },
];

// Mindspace tab - the surfaces the user can spend time on outside a
// lesson. Revision is intentionally excluded (separate surface no longer
// tracked toward Progress).
const MINDSPACE_GROUPS: SectionGroup[] = [
  {
    key: 'quick_bytes',
    label: 'Quick Bytes',
    colorKey: 's1',
    types: ['quick_bytes'],
  },
  {
    key: 'practice_arena',
    label: 'Practice Arena',
    colorKey: 's2',
    types: ['practice_arena'],
  },
  {
    key: 'knowledge_archive',
    label: 'Knowledge Archive',
    colorKey: 's3',
    types: ['knowledge_archive'],
  },
];

function rollup(
  breakdown: Record<string, number>,
  groups: SectionGroup[]
): Array<{
  key: string;
  label: string;
  colorKey: keyof ChartSeries;
  hours: number;
}> {
  return groups
    .map(g => ({
      key: g.key,
      label: g.label,
      colorKey: g.colorKey,
      hours: g.types.reduce((sum, t) => sum + (breakdown[t] ?? 0), 0),
    }))
    .filter(g => g.hours > 0)
    .sort((a, b) => b.hours - a.hours);
}

// Sub-hour durations render in minutes so a couple of minutes of real activity
// doesn't collapse to a misleading "0.0h", with a 1m floor so a few seconds
// still reads as something.
//
// Zero is handled separately, and must be: the per-group bars are all > 0
// (`rollup` filters true zeros), but the HEADER total is `formatDuration` of
// their sum, which is 0 for a period with no activity at all. Without this
// branch the 1m floor turned that into "1m total" — a fabricated minute
// sitting directly above an "no activity in this period yet" placeholder.
function formatDuration(hours: number): string {
  if (hours >= 1) return `${hours.toFixed(1)}h`;
  if (hours <= 0) return '0m';
  return `${Math.max(1, Math.round(hours * 60))}m`;
}

/**
 * The bar rows only. Deliberately NOT a skeleton for the whole card: the
 * heading and the Lesson / Mindspace toggle above it are driven by local
 * `scope` state, not by the query, so they render for real while this shows.
 * Shimmering them would fake a wait for something already known, and it would
 * make the toggle dead for the duration of the load for no reason.
 *
 * Keeping the real buttons mounted has a second effect worth naming: the page's
 * scroll region needs focusable content to stay keyboard-scrollable (axe's
 * scrollable-region-focusable), and during the initial load these are the only
 * focusable things in it.
 */
function ActivityRowsSkeleton() {
  return (
    <div className='space-y-2.5'>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className='flex items-center gap-3'>
          <Skeleton className='h-2.5 w-2.5 shrink-0 rounded-sm' />
          <Skeleton className='h-3 w-36 shrink-0' />
          <Skeleton className='h-2 min-w-0 flex-1 rounded-full' />
          <Skeleton className='h-3 w-16 shrink-0' />
        </div>
      ))}
    </div>
  );
}

export function MyLessonSection({
  data,
  isLoading,
  initialScope = 'lesson',
}: MyLessonSectionProps) {
  const [scope, setScope] = useState<ActivityScope>(initialScope);
  const chartColors = useChartColors();

  if (!isLoading && !data) return null;

  const groups = scope === 'lesson' ? LESSON_GROUPS : MINDSPACE_GROUPS;
  const sectionRollup = data ? rollup(data.activity_breakdown, groups) : [];
  const breakdownTotal = sectionRollup.reduce((s, g) => s + g.hours, 0);

  return (
    <Card elevation='none' className='p-5'>
      <div className='mb-4 flex items-center justify-between'>
        <h3 className='text-sm font-semibold text-foreground'>
          {scope === 'lesson'
            ? 'Lesson activity by section'
            : 'Mindspace activity by surface'}
        </h3>
        {isLoading ? (
          <Skeleton className='h-3 w-20' />
        ) : (
          <span className='text-xs tabular-nums text-muted-foreground'>
            {formatDuration(breakdownTotal)} total
          </span>
        )}
      </div>

      <div className='mb-4 flex items-center'>
        <SegmentedControl
          label='Activity scope'
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={setScope}
          size='sm'
          // Drawn small, with an invisible 44px tap area on phones.
          segmentClassName='tap-target'
        />
      </div>

      {isLoading ? (
        <ActivityRowsSkeleton />
      ) : sectionRollup.length === 0 ? (
        // Inline rather than a shared `EmptySlot` component. That component
        // existed to be the one sub-area empty pattern for this feature and
        // never got a second caller: Streaks and Retention each hand-rolled
        // their own `text-xs text-muted-foreground` line instead, so it was an
        // abstraction with one consumer and two unused `size` variants. The
        // padding keeps the card's height stable when bars appear.
        <div className='flex flex-col items-center justify-center gap-1.5 px-4 py-6 text-center'>
          <Inbox className='h-4 w-4 text-muted-foreground' />
          <p className='text-sm leading-snug text-muted-foreground'>
            No {scope === 'lesson' ? 'lesson' : 'mindspace'} activity in this
            period yet.
          </p>
          <p className='text-xs text-muted-foreground'>
            {scope === 'lesson'
              ? 'Open a lesson tab to start logging time here.'
              : 'Visit Quick Bytes, Practice Arena, or Knowledge Archive to populate this view.'}
          </p>
        </div>
      ) : (
        <div className='space-y-2.5'>
          {sectionRollup.map(g => {
            const pct = (g.hours / breakdownTotal) * 100;
            return (
              <div key={g.key} className='flex items-center gap-3'>
                <span
                  className='h-2.5 w-2.5 shrink-0 rounded-sm'
                  style={{ backgroundColor: chartColors.series[g.colorKey] }}
                />
                <span className='w-36 shrink-0 truncate text-xs font-medium text-muted-foreground'>
                  {g.label}
                </span>
                {/* A real `progressbar`, not two nested divs. The hand-rolled
                    version announced nothing, so the whole activity breakdown
                    was invisible to a screen reader; `ariaValueText` gives it
                    the label and the duration rather than a bare percentage. */}
                <ProgressBar
                  value={pct}
                  size='md'
                  className='min-w-0 flex-1'
                  fillColor={chartColors.series[g.colorKey]}
                  ariaLabel={g.label}
                  ariaValueText={`${g.label}: ${formatDuration(g.hours)}, ${
                    pct < 1 ? 'under 1' : Math.round(pct)
                  }% of total`}
                />
                <span className='shrink-0 text-xs text-muted-foreground'>
                  <span className='font-semibold tabular-nums text-foreground'>
                    {formatDuration(g.hours)}
                  </span>{' '}
                  <span className='text-muted-foreground'>·</span>{' '}
                  <span className='tabular-nums'>
                    {pct < 1 ? '<1%' : `${Math.round(pct)}%`}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
